import HTMLReactParser from 'html-react-parser';
import { Box, Card, Dialog, Flex, Text, IconButton, Heading, ScrollArea } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';

import { CloseIcon, LanguageIcon, NavigationIcon } from 'shared/icons';
import { JobViewStore } from './job-view.store';
import './job-view-dialog.scss';

const JobViewDialogView = ({ jobView }: { jobView: JobViewStore }) => {
   return (
      <Box className="job-view-dialog">
         <Flex justify="between" align="baseline">
            <Dialog.Title color="orange"></Dialog.Title>

            <Dialog.Close>
               <IconButton size="1" color="gray">
                  <CloseIcon width={20} height={20} />
               </IconButton>
            </Dialog.Close>
         </Flex>

         <Flex className="job-header-info" gap="2">
            <Box>
               <Heading as="h2" weight="regular" color="gray" highContrast>
                  Назва вакансії: {jobView.jobTitle}
               </Heading>
            </Box>
         </Flex>

         <ScrollArea scrollbars="vertical" type="auto" className="dialog__scroll-area">
            <Flex
               pt="4"
               gap="4"
               mr="2"
               direction={{
                  initial: 'column',
                  md: 'row',
               }}
            >
               <Flex flexGrow="1" direction="column">
                  <Flex py="4" px="2" direction="column">
                     <Heading as="h3" weight="regular" size="5">
                        Опис вакансії
                     </Heading>
                     <Box py="2">{HTMLReactParser(jobView.jobDescription)}</Box>
                  </Flex>

                  <Flex py="4" px="2" direction="column">
                     <Heading as="h3" weight="regular" size="5">
                        Необхідні навички
                     </Heading>
                     <Box py="2">
                        <ul className="attr-list">
                           {jobView.skills.map((skill) => {
                              return (
                                 <li className="attr-list__item" key={skill.id}>
                                    <Text color="gray" className="li-marker">
                                       <NavigationIcon />
                                    </Text>

                                    {skill.name}
                                 </li>
                              );
                           })}
                        </ul>
                     </Box>
                  </Flex>

                  <Flex py="4" px="2" direction="column">
                     <Heading as="h3" weight="regular" size="5">
                        Знання мов
                     </Heading>
                     <Box py="2">
                        <ul className="attr-list">
                           {jobView.languages.map((l) => {
                              return (
                                 <li className="attr-list__item" key={l.id}>
                                    <Text color="gray" className="li-marker">
                                       <NavigationIcon />
                                    </Text>
                                    {l.lang}
                                 </li>
                              );
                           })}
                        </ul>
                     </Box>
                  </Flex>
               </Flex>

               <Box className="right-column">
                  <Card className="company-card">
                     <Heading as="h3" weight="regular" mb="4" size="5">
                        Огляд вакансії
                     </Heading>
                     <Flex gap="2" direction="column">
                        <Flex direction="column">
                           <Text weight="medium" color="gray" highContrast>
                              Дата розміщення:
                           </Text>
                           <Text color="gray">{/*  unkonoe */}</Text>
                        </Flex>

                        <Flex direction="column">
                           <Text weight="medium" color="gray" highContrast>
                              Локація:
                           </Text>
                           <Flex gap="1">
                              <Text color="gray">
                                 <NavigationIcon className="li-marker" />
                              </Text>

                              <Text color="gray">{jobView.jobLocation}</Text>
                           </Flex>
                        </Flex>

                        <Flex direction="column">
                           <Text weight="medium" color="gray" highContrast>
                              Категорія:
                           </Text>
                           <Text color="gray">
                              <ul className="attr-list">
                                 {jobView.categories.map((cat) => {
                                    return (
                                       <li className="attr-list__item" key={cat.id}>
                                          <Text color="gray" className="li-marker">
                                             <NavigationIcon />
                                          </Text>

                                          {cat.name}
                                       </li>
                                    );
                                 })}
                              </ul>
                           </Text>
                        </Flex>

                        <Flex direction="column">
                           <Text weight="medium" color="gray" highContrast>
                              Спеціалізація:
                           </Text>
                           <Text color="gray">
                              <ul className="attr-list">
                                 {jobView.subCategbories.map((cat) => {
                                    return (
                                       <li className="attr-list__item" key={cat.id}>
                                          <Text color="gray" className="li-marker">
                                             <NavigationIcon />
                                          </Text>

                                          {cat.name}
                                       </li>
                                    );
                                 })}
                              </ul>
                           </Text>
                        </Flex>

                        <Flex direction="column">
                           <Text weight="medium" color="gray" highContrast>
                              Тип зайнятості:
                           </Text>
                           <Flex gap="1">
                              <Text color="gray">
                                 <NavigationIcon className="li-marker" />
                              </Text>

                              <Text color="gray">{jobView.workType}</Text>
                           </Flex>
                        </Flex>

                        <Flex direction="column">
                           <Text weight="medium" color="gray" highContrast>
                              Досвід:
                           </Text>
                           <Flex gap="1">
                              <Text color="gray">
                                 <NavigationIcon className="li-marker" />
                              </Text>

                              <Text color="gray">{jobView.experience}</Text>
                           </Flex>
                        </Flex>

                        <Flex direction="column">
                           <Text weight="medium" color="gray" highContrast>
                              Зарплата:
                           </Text>
                           <Flex gap="1">
                              <Text color="gray">
                                 <NavigationIcon className="li-marker" />
                              </Text>

                              <Text color="gray">{jobView.salary}</Text>
                           </Flex>
                        </Flex>
                     </Flex>
                  </Card>

                  <Card className="company-card" mt="4">
                     <Heading as="h3" weight="regular" mb="4" size="5">
                        Контактна інформація
                     </Heading>
                     <Flex gap="2">
                        <Box className="logo-container">
                           <img alt="Company name" src={jobView.logoSrc} />
                        </Box>

                        <Box>
                           <Flex direction="column">
                              <Text weight="medium" color="gray">
                                 Назва компанії:
                              </Text>
                              <Text color="gray"> {jobView.companyName}</Text>
                           </Flex>

                           <Flex mt="2" gap="1" align="center">
                              <Text weight="medium" color="gray">
                                 Вебсайт:
                              </Text>
                              <Text color="gray">
                                 <a href={jobView.website} target="_blank" rel="noreferrer">
                                    <LanguageIcon width={20} height={20} />
                                 </a>
                              </Text>
                           </Flex>
                        </Box>
                     </Flex>
                  </Card>
               </Box>
            </Flex>
         </ScrollArea>
      </Box>
   );
};

export const JobViewDialog = observer(JobViewDialogView);
